import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle, FileText, Calendar, Percent, Ban } from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import './ViolationTrendPage.css';
import { useViolationTrendTimeseries, useJobFilterOptions } from '../hooks/useApi';
import MultiSelectFilter from '../components/MultiSelectFilter';

const formatDateForInput = (iso: string | undefined): string => (iso ? iso.slice(0, 10) : '');

const INACTIVE_LEGEND_COLOR = '#cbd5e1';

const CHART_SERIES = [
    { key: 'patrolled_url_count', name: 'パトロールURL数', color: '#000000', axis: 'counts' as const },
    { key: 'violation_url_count', name: '違反があるURL数', color: '#f59e0b', axis: 'counts' as const },
    { key: 'violation_site_rate_pct', name: '違反サイト率', color: '#3b82f6', axis: 'rate' as const },
];

const ViolationTrendPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [createdAfter, setCreatedAfter] = React.useState(searchParams.get('after') || '');
    const [createdBefore, setCreatedBefore] = React.useState(searchParams.get('before') || '');
    const [selectedGuidelines, setSelectedGuidelines] = React.useState<string[]>(
        (searchParams.get('guidelines') || '').split(',').filter(Boolean)
    );
    const [bucket, setBucket] = React.useState<'month' | 'week'>(
        searchParams.get('bucket') === 'week' ? 'week' : 'month'
    );
    const [hiddenSeries, setHiddenSeries] = React.useState<Set<string>>(new Set());

    const toggleSeries = (key: string) => {
        setHiddenSeries((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // Keep URL in sync so filters survive a refresh.
    useEffect(() => {
        const params: Record<string, string> = {};
        if (createdAfter) params.after = createdAfter;
        if (createdBefore) params.before = createdBefore;
        if (selectedGuidelines.length) params.guidelines = selectedGuidelines.join(',');
        if (bucket !== 'month') params.bucket = bucket;
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdAfter, createdBefore, selectedGuidelines, bucket]);

    const timeseriesParams = useMemo(
        () => ({
            bucket,
            guidelineId: selectedGuidelines.length ? selectedGuidelines.join(',') : undefined,
            createdAfter: createdAfter || undefined,
            createdBefore: createdBefore || undefined,
        }),
        [bucket, selectedGuidelines, createdAfter, createdBefore]
    );

    const { data: timeseriesData, loading: timeseriesLoading, error: timeseriesError } =
        useViolationTrendTimeseries(timeseriesParams);
    const { data: jobFilterOptionsData } = useJobFilterOptions();

    const chartPoints = useMemo(() => timeseriesData?.points || [], [timeseriesData]);

    const combinedChartData = useMemo(
        () =>
            chartPoints.map((p) => ({
                ...p,
                violation_site_rate_pct: p.violation_site_rate != null ? p.violation_site_rate * 100 : null,
            })),
        [chartPoints]
    );

    const guidelineOptions = useMemo(() => {
        const guidelines = (jobFilterOptionsData as any)?.data?.guidelines || [];
        return guidelines.map((g: any) => ({ label: g.name || g.id, value: g.id }));
    }, [jobFilterOptionsData]);

    const totals = useMemo(
        () =>
            chartPoints.reduce(
                (acc, p) => ({
                    patrolledUrlCount: acc.patrolledUrlCount + p.patrolled_url_count,
                    violationUrlCount: acc.violationUrlCount + p.violation_url_count,
                }),
                { patrolledUrlCount: 0, violationUrlCount: 0 }
            ),
        [chartPoints]
    );

    const overallViolationSiteRate =
        totals.patrolledUrlCount > 0 ? (totals.violationUrlCount / totals.patrolledUrlCount) * 100 : null;

    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">違反件数推移ダッシュボード</h1>
                <p className="page-description">
                    パトロールしたURL数・違反があるURL数の推移を月別または週別に集計します。ガイドラインを複数選択すると合計値、未選択の場合は全ガイドラインの合計を表示します。
                </p>
            </div>

            {!timeseriesLoading && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon violation-trend-stat-icon-neutral">
                            <FileText size={24} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{totals.patrolledUrlCount.toLocaleString()}</div>
                            <div className="stat-label">パトロールしたURL数</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon warning">
                            <Ban size={24} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{totals.violationUrlCount.toLocaleString()}</div>
                            <div className="stat-label">違反があるURL数</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon danger">
                            <Percent size={24} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">
                                {overallViolationSiteRate != null ? `${overallViolationSiteRate.toFixed(1)}%` : '—'}
                            </div>
                            <div className="stat-label">違反サイト率</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="filters-section">
                <div className="filters-row">
                    <div className="filter-controls">
                        <div className="multi-select-filter violation-trend-date-filter">
                            <label className="filter-label">
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Calendar size={12} />
                                    期間（ジョブ作成日時）
                                </span>
                            </label>
                            <div className="violation-trend-date-range">
                                <input
                                    type="date"
                                    value={formatDateForInput(createdAfter)}
                                    onChange={(e) => setCreatedAfter(e.target.value)}
                                    className="violation-trend-date-input"
                                />
                                <span className="violation-trend-date-separator">〜</span>
                                <input
                                    type="date"
                                    value={formatDateForInput(createdBefore)}
                                    onChange={(e) => setCreatedBefore(e.target.value)}
                                    className="violation-trend-date-input"
                                />
                            </div>
                        </div>

                        <MultiSelectFilter
                            label={<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FileText size={12} />ガイドライン</span>}
                            options={guidelineOptions}
                            selectedValues={selectedGuidelines}
                            onSelectionChange={setSelectedGuidelines}
                            placeholder="すべてのガイドライン（合計）"
                        />
                    </div>
                </div>
            </div>

            <div className="violation-trend-chart-section">
                <div className="violation-trend-chart-header">
                    <h2 className="violation-trend-chart-title">URL数・違反サイト率</h2>
                    <div className="violation-trend-mode-toggle">
                        <button
                            type="button"
                            className={`violation-trend-mode-btn ${bucket === 'month' ? 'active' : ''}`}
                            onClick={() => setBucket('month')}
                        >
                            月別
                        </button>
                        <button
                            type="button"
                            className={`violation-trend-mode-btn ${bucket === 'week' ? 'active' : ''}`}
                            onClick={() => setBucket('week')}
                        >
                            週別
                        </button>
                    </div>
                </div>

                {timeseriesError && (
                    <div className="error-state">
                        <AlertTriangle size={20} />
                        <span>{timeseriesError}</span>
                    </div>
                )}

                {timeseriesLoading ? (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>読み込み中...</span>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={360}>
                        <LineChart data={combinedChartData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                            <YAxis
                                yAxisId="counts"
                                tick={{ fontSize: 11 }}
                                label={{ value: '件数', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                            />
                            <YAxis
                                yAxisId="rate"
                                orientation="right"
                                tick={{ fontSize: 11 }}
                                tickFormatter={(v: number) => `${v}%`}
                                label={{ value: '違反サイト率', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                            />
                            <Tooltip
                                formatter={(value: number | string, name: string) =>
                                    name === '違反サイト率' && typeof value === 'number' ? `${value.toFixed(1)}%` : value
                                }
                            />
                            <Legend
                                content={
                                    <div className="violation-trend-legend">
                                        {CHART_SERIES.map((s) => {
                                            const isHidden = hiddenSeries.has(s.key);
                                            return (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    className={`violation-trend-legend-item${isHidden ? ' inactive' : ''}`}
                                                    onClick={() => toggleSeries(s.key)}
                                                >
                                                    <span
                                                        className="violation-trend-legend-swatch"
                                                        style={{ backgroundColor: isHidden ? INACTIVE_LEGEND_COLOR : s.color }}
                                                    />
                                                    {s.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                }
                            />
                            {CHART_SERIES.map((s) => (
                                <Line
                                    key={s.key}
                                    yAxisId={s.axis}
                                    type="monotone"
                                    dataKey={s.key}
                                    name={s.name}
                                    stroke={s.color}
                                    strokeDasharray={s.axis === 'rate' ? '5 5' : undefined}
                                    dot={false}
                                    connectNulls
                                    hide={hiddenSeries.has(s.key)}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}

                {!timeseriesLoading && chartPoints.length === 0 && (
                    <div className="violation-trend-empty">該当するデータがありません</div>
                )}
            </div>
        </div>
    );
};

export default ViolationTrendPage;
